/**
 * Peças de tela da versão mobile — as mesmas classes do SIAG (`card`, `btn-*`,
 * `badge-*`), organizadas para o polegar.
 *
 * Regra das telas do módulo: no computador nada muda. Na casca mobile:
 * - o título grande da página sai (o cabeçalho da casca já mostra o título);
 * - tabela vira lista de cards no celular e no tablet em pé (< lg); no tablet
 *   deitado a tabela volta, porque cabe;
 * - filtros viram "chips" que rolam para o lado.
 */
import type { ReactNode } from "react";
import { useMobileLayout } from "./useMobileLayout";

/** Esta tela está dentro da casca mobile? */
export function useMobile(): boolean {
  return useMobileLayout().layout === "mobile";
}

/**
 * Tabela no computador; cards na casca mobile — em qualquer largura.
 *
 * Começou com tabela no tablet deitado, mas a letra da casca é maior (ver
 * `html.siag-mobile`) e numa oficina a leitura importa mais que a densidade: a
 * tabela de 6 colunas cortava a última no tablet de 1280px (medido em 21/10/2026).
 */
export function TableOrCards({ table, cards }: { table: ReactNode; cards: ReactNode }) {
  return <>{useMobile() ? cards : table}</>;
}

/** Contêiner das listas de cards: 1 coluna no celular, 2 a partir do tablet em pé. */
export const CARD_GRID = "grid grid-cols-1 gap-2.5 md:grid-cols-2";

/** Linha "chips" que rola para o lado, sem barra de rolagem visível. */
export function ChipRow({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {children}
    </div>
  );
}

export function Chip({ on, onClick, children }: { on?: boolean; onClick?: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex min-h-[2.25rem] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[0.8125rem] font-medium transition-colors ${
        on ? "border-tm-500 bg-tm-500 text-white" : "border-surface-border bg-white text-gray-600"
      }`}
    >
      {children}
    </button>
  );
}

/** Card de lista: área de toque inteira, com chevron implícito no `onClick`/link do chamador. */
export function MobileCard({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`card p-3.5 ${className}`}>{children}</div>;
}

/** Barra de ação fixa acima da barra inferior (ou no rodapé, no tablet deitado). */
export function MobileActionBar({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-x-0 z-20 border-t border-surface-border bg-white px-4 py-3 bottom-[calc(3.625rem+env(safe-area-inset-bottom))] lg:bottom-0 lg:pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex max-w-6xl gap-2">{children}</div>
    </div>
  );
}
